import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Copy, ExternalLink, Archive, CheckCircle2, XCircle, Phone,
  MapPin, User, Hash, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';

const MATCH_KEY = {
  tiktok_user_id: 'dupes.matched_user_id',
  phone: 'dupes.matched_phone',
  name_address: 'dupes.matched_name_address',
};

function MemberCard({ m, t, neutral = false }) {
  const keep = !neutral && m.role === 'keep';
  return (
    <div
      className="flex-1 min-w-0 rounded-xl p-3.5"
      style={{
        background: 'var(--surface-2)',
        border: `1px solid ${keep ? 'rgba(34,197,94,0.45)' : 'var(--border-default)'}`,
        boxShadow: keep ? 'inset 0 0 0 1px rgba(34,197,94,0.08)' : 'none',
      }}
      data-testid={`dupes-member-${m.qbo_customer_id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="rounded-full px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{
            background: keep ? 'rgba(34,197,94,0.12)'
              : neutral ? 'var(--border-subtle)' : 'rgba(212,168,67,0.10)',
            color: keep ? '#22C55E'
              : neutral ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))',
          }}
          data-testid={`dupes-member-role-${m.qbo_customer_id}`}
        >
          {neutral ? t('dupes.role_neutral')
            : keep ? t('dupes.role_keep') : t('dupes.role_archive')}
        </span>
        {!m.active && (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('dupes.inactive')}
          </span>
        )}
      </div>
      <div className="truncate text-[13px] font-semibold text-foreground" title={m.display_name}>
        {m.display_name || '—'}
      </div>
      <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Hash className="h-3 w-3 shrink-0 opacity-60" />
          <span className="font-mono">{m.qbo_customer_id}</span>
          <a
            href={m.qbo_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline"
            data-testid={`dupes-qbo-link-${m.qbo_customer_id}`}
          >
            <ExternalLink className="h-3 w-3" /> QBO
          </a>
        </div>
        <div className="flex items-start gap-1.5">
          <Phone className="h-3 w-3 mt-[2px] shrink-0 opacity-60" />
          <span className="font-mono break-all">
            {(m.phones || []).join(' · ') || t('dupes.no_phone')}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <MapPin className="h-3 w-3 mt-[2px] shrink-0 opacity-60" />
          <span className="break-all">
            {(m.addresses || []).join(' · ') || m.addr_key_v2 || t('dupes.no_address')}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <User className="h-3 w-3 mt-[2px] shrink-0 opacity-60" />
          <span className="font-mono break-all">
            {(m.tiktok_user_ids || []).join(' · ') || t('dupes.no_user_id')}
          </span>
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px]">
        <span
          className="rounded-md px-1.5 py-0.5 font-mono"
          style={{ background: 'var(--border-subtle)' }}
          data-testid={`dupes-member-tx-${m.qbo_customer_id}`}
        >
          {m.transactions} {t('dupes.transactions')}
        </span>
        {m.open_balance > 0 && (
          <span className="rounded-md px-1.5 py-0.5 font-mono text-[hsl(var(--status-danger))]">
            {t('dupes.open_balance')}: {m.open_balance}
          </span>
        )}
        {(neutral ? [] : (m.blockers || [])).map((b) => (
          <span
            key={b}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[hsl(var(--status-danger))]"
            style={{ background: 'rgba(239,68,68,0.08)' }}
            data-testid={`dupes-blocker-${b}-${m.qbo_customer_id}`}
          >
            <ShieldAlert className="h-3 w-3" /> {t(`dupes.blocker_${b}`)}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function DuplicateGroupCard({ group, selected, onToggleSelect }) {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const [plan, setPlan] = useState(null);
  const [showMerge, setShowMerge] = useState(false);
  const [showIgnore, setShowIgnore] = useState(false);
  const [reason, setReason] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['dupe-groups'] });
  const errText = (err) => {
    const d = err?.response?.data?.detail;
    const code = typeof d === 'string' ? d : d?.reason;
    return t(`dupes.error_${code}`) === `dupes.error_${code}`
      ? (code || t('dupes.error_generic'))
      : t(`dupes.error_${code}`);
  };

  const dryRun = useMutation({
    mutationFn: async () => (await api.post(
      `/admin/customers/duplicate-groups/${group.group_id}/resolve`,
      { dry_run: true })).data,
    onSuccess: (d) => setPlan(d.plan),
    onError: (err) => toast.error(errText(err)),
  });

  const apply = useMutation({
    mutationFn: async () => (await api.post(
      `/admin/customers/duplicate-groups/${group.group_id}/resolve`,
      { dry_run: false })).data,
    onSuccess: () => {
      toast.success(t('dupes.resolved_toast'));
      setPlan(null);
      invalidate();
    },
    onError: (err) => toast.error(errText(err)),
  });

  const markMerged = useMutation({
    mutationFn: async () => (await api.post(
      `/admin/customers/duplicate-groups/${group.group_id}/mark-merged`)).data,
    onSuccess: () => {
      toast.success(t('dupes.merged_toast'));
      setShowMerge(false);
      invalidate();
    },
    onError: (err) => toast.error(errText(err)),
  });

  const ignore = useMutation({
    mutationFn: async () => (await api.post(
      `/admin/customers/duplicate-groups/${group.group_id}/ignore`,
      { reason })).data,
    onSuccess: () => {
      toast.success(t('dupes.ignored_toast'));
      setShowIgnore(false);
      invalidate();
    },
    onError: (err) => toast.error(errText(err)),
  });

  const isSystem = group.resolution_type === 'system';
  const isLikelyDifferent = group.resolution_type === 'likely_different';
  const isPending = group.status === 'pending';
  const explanation = group.explanation?.[lang] || group.explanation?.es;
  const steps = group.merge_instructions?.[lang] || group.merge_instructions?.es || [];
  const bodyText = (() => {
    if (group.status === 'ignored') {
      return group.ignore_reason
        ? `${t('dupes.ignored_note')} — ${group.ignore_reason}`
        : t('dupes.ignored_note');
    }
    if (group.status === 'resolved') return t('dupes.resolved_note');
    return explanation;
  })();

  return (
    <Card
      className="bg-card border-border"
      data-testid={`dupes-group-${group.group_id}`}
    >
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {isSystem && isPending && (
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(group.group_id)}
              data-testid={`dupes-select-${group.group_id}`}
            />
          )}
          <span
            className="rounded-full px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{
              background: isSystem ? 'rgba(34,197,94,0.10)'
                : isLikelyDifferent ? 'rgba(239,68,68,0.10)' : 'rgba(212,168,67,0.12)',
              color: isSystem ? '#22C55E'
                : isLikelyDifferent ? 'hsl(var(--status-danger))' : 'hsl(var(--primary))',
            }}
            data-testid={`dupes-type-${group.group_id}`}
          >
            {isSystem ? t('dupes.type_system')
              : isLikelyDifferent ? t('dupes.type_likely_different')
                : t('dupes.type_manual')}
          </span>
          {(group.matched_by || []).map((k) => (
            <span
              key={k}
              className="rounded-full px-2 py-[3px] text-[10px] font-medium text-muted-foreground"
              style={{ background: 'var(--border-subtle)' }}
            >
              {t(MATCH_KEY[k] || k)}
            </span>
          ))}
          {group.has_pending_identity_review && (
            <span className="rounded-full px-2 py-[3px] text-[10px] font-medium text-[hsl(var(--primary))]">
              {t('dupes.in_identity_review')}
            </span>
          )}
          {!isPending && (
            <span
              className="rounded-full px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground"
              style={{ background: 'var(--border-subtle)' }}
              data-testid={`dupes-status-${group.group_id}`}
            >
              {t(`dupes.status_${group.status}`)}
              {group.resolved_by ? ` · ${group.resolved_by}` : ''}
            </span>
          )}
          {group.last_error && (
            <span className="text-[11px] text-[hsl(var(--status-danger))]" data-testid={`dupes-error-${group.group_id}`}>
              {group.last_error}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row">
          {group.members.map((m) => (
            <MemberCard
              key={m.qbo_customer_id}
              m={m}
              t={t}
              neutral={isLikelyDifferent}
            />
          ))}
        </div>

        <p
          className="mt-3 rounded-lg p-3 text-[12px] leading-relaxed text-foreground/90"
          style={{ background: 'var(--border-subtle)' }}
          data-testid={`dupes-explanation-${group.group_id}`}
        >
          {bodyText}
        </p>

        {isPending && (
          <div className="mt-3 flex flex-wrap gap-2">
            {isSystem && (
              <Button
                size="sm"
                onClick={() => dryRun.mutate()}
                disabled={dryRun.isPending}
                data-testid={`dupes-resolve-${group.group_id}`}
              >
                {dryRun.isPending
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Archive className="h-4 w-4 mr-2" />}
                {t('dupes.resolve')}
              </Button>
            )}
            {!isSystem && !isLikelyDifferent && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowMerge(true)}
                data-testid={`dupes-howto-${group.group_id}`}
              >
                {t('dupes.howto_merge')}
              </Button>
            )}
            <Button
              size="sm"
              variant={isLikelyDifferent ? 'default' : 'ghost'}
              onClick={() => setShowIgnore(true)}
              data-testid={`dupes-ignore-${group.group_id}`}
            >
              <XCircle className="h-4 w-4 mr-2" /> {t('dupes.not_same_person')}
            </Button>
          </div>
        )}
      </CardContent>

      {/* Confirmation of a system-resolvable group (after the dry-run) */}
      <Dialog open={!!plan} onOpenChange={(v) => { if (!v) setPlan(null); }}>
        <DialogContent data-testid="dupes-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('dupes.confirm_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-[12px]">
            <p className="leading-relaxed">{explanation}</p>
            <div className="rounded-lg p-2.5 font-mono text-[11px]" style={{ background: 'var(--border-subtle)' }}>
              <div>{t('dupes.keeps')}: {plan?.keep_name} ({plan?.keep_id})</div>
              {(plan?.archive || []).map((a) => (
                <div key={a.qbo_customer_id}>
                  {t('dupes.archives')}: {a.display_name} ({a.qbo_customer_id}) → Active=false
                </div>
              ))}
              <div>{t('dupes.transactions_moved')}: 0</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setPlan(null)} data-testid="dupes-confirm-cancel">
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => apply.mutate()}
              disabled={apply.isPending}
              data-testid="dupes-confirm-apply"
            >
              {apply.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('dupes.confirm_apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual merge instructions */}
      <Dialog open={showMerge} onOpenChange={setShowMerge}>
        <DialogContent data-testid="dupes-merge-dialog">
          <DialogHeader>
            <DialogTitle>{t('dupes.howto_merge')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12px]">
            <p className="leading-relaxed">{explanation}</p>
            <ol className="list-decimal space-y-1.5 pl-5 leading-relaxed">
              {steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  navigator.clipboard?.writeText(group.keep_name || '');
                  toast.success(t('dupes.copied'));
                }}
                data-testid="dupes-copy-name"
              >
                <Copy className="h-4 w-4 mr-2" /> {t('dupes.copy_keep_name')}
              </Button>
              {group.members.filter((m) => m.role === 'archive').map((m) => (
                <a
                  key={m.qbo_customer_id}
                  href={m.qbo_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--primary))] hover:underline"
                  data-testid={`dupes-open-qbo-${m.qbo_customer_id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" /> {t('dupes.open_in_qbo')} · {m.display_name}
                </a>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowMerge(false)} data-testid="dupes-merge-cancel">
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => markMerged.mutate()}
              disabled={markMerged.isPending}
              data-testid="dupes-mark-merged"
            >
              {markMerged.isPending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {t('dupes.already_merged')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Not the same person */}
      <Dialog open={showIgnore} onOpenChange={setShowIgnore}>
        <DialogContent data-testid="dupes-ignore-dialog">
          <DialogHeader>
            <DialogTitle>{t('dupes.not_same_person')}</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">{t('dupes.ignore_hint')}</p>
          <Input
            placeholder={t('dupes.ignore_reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="dupes-ignore-reason"
          />
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowIgnore(false)} data-testid="dupes-ignore-cancel">
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => ignore.mutate()}
              disabled={ignore.isPending}
              data-testid="dupes-ignore-confirm"
            >
              {ignore.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
